import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Lightweight hover/focus tooltip, portalled to <body> (same pattern as
// DateTimePicker / SearchableSelect) so it's never clipped by a sticky
// table header or any other overflow-hidden ancestor.
//
// Usage:
//   <Tooltip content="Explains what this is for">
//     <span>Trigger</span>
//   </Tooltip>
//
//   // Brand pill to the right of the trigger (e.g. sidebar nav labels)
//   <Tooltip content="User Role Module" placement="right" variant="brand" fullWidth>
//     <NavLink ... />
//   </Tooltip>
//
// Props:
//   placement — "top" (default) | "right"
//   variant   — "default" (surface card) | "brand" (primary pill, white text)
//   fullWidth — wrapper is a plain full-width block (not flex) so the child
//               keeps its normal block layout and can shrink/truncate
export default function Tooltip({
  content,
  children,
  className = "",
  placement = "top",
  variant = "default",
  fullWidth = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);

  const show = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords(
        placement === "right"
          ? { top: rect.top + rect.height / 2, left: rect.right + 12 }
          : { top: rect.top - 8, left: rect.left + rect.width / 2 }
      );
    }
    setIsOpen(true);
  }, [placement]);

  const hide = useCallback(() => setIsOpen(false), []);

  // Fixed-position tooltip would drift from its trigger if an ancestor scrolls.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [isOpen, hide]);

  if (!content) return children;

  const isRight = placement === "right";
  const isBrand = variant === "brand";

  const bubbleClass = isBrand
    ? "whitespace-nowrap rounded-lg bg-primary px-3 py-1.5 text-[13px] font-medium text-white shadow-lg"
    : "max-w-[220px] rounded-lg border border-border bg-surface px-3 py-2 text-[11px] leading-relaxed text-text_secondary shadow-2xl";

  const arrowClass = isBrand
    ? "h-2 w-2 rotate-45 bg-primary"
    : "h-2 w-2 rotate-45 border-border bg-surface";

  return (
    <span
      ref={triggerRef}
      className={`${fullWidth ? "block w-full min-w-0" : "inline-flex"} ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {isOpen &&
        coords &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform: isRight ? "translateY(-50%)" : "translate(-50%, -100%)",
              zIndex: 9999,
            }}
            className={`pointer-events-none ${isRight ? "flex items-center" : ""}`}
          >
            {isRight ? (
              <>
                <div className={`${arrowClass} -mr-1 ${isBrand ? "" : "border-b border-l"}`} />
                <div className={bubbleClass}>{content}</div>
              </>
            ) : (
              <>
                <div className={bubbleClass}>{content}</div>
                <div className={`${arrowClass} mx-auto -translate-y-1/2 ${isBrand ? "" : "border-b border-r"}`} />
              </>
            )}
          </div>,
          document.body
        )}
    </span>
  );
}
